<template>
  <div
    class="upload-drop-zone ad-media-drop-zone"
    :class="{
      'is-dragover': isDragging,
      'has-files': fileCount > 0,
      'is-empty': fileCount === 0,
      'at-max': fileCount >= maxFiles,
    }"
    @click="onZoneClick"
    @dragenter.prevent="isDragging = true"
    @dragover.prevent="isDragging = true"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="fileInput"
      type="file"
      class="d-none"
      :accept="acceptAttribute"
      :multiple="fileCount < maxFiles"
      @change="onInputChange"
    >

    <div
      v-if="fileCount > 0"
      class="media-preview-grid"
      @click.stop
    >
      <div
        v-for="(file, idx) in normalizedFiles"
        :key="previewKey(file, idx)"
        class="media-preview-cell"
      >
        <div class="media-preview-box">
          <img
            v-if="isImageFile(file)"
            :src="previewUrls[idx]"
            alt=""
            class="media-preview-img"
          >
          <video
            v-else-if="isVideoFile(file)"
            :src="previewUrls[idx]"
            class="media-preview-video"
            muted
            playsinline
          />
          <button
            type="button"
            class="media-preview-clear"
            :aria-label="`Remove ${file.name}`"
            @click.stop="removeAt(idx)"
          >
            <feather-icon
              icon="XIcon"
              size="16"
            />
          </button>
        </div>
        <p
          class="media-file-name text-truncate"
          :title="file.name"
        >
          {{ file.name }}
        </p>
      </div>
    </div>

    <div
      v-if="fileCount < maxFiles"
      class="add-more-block"
      :class="{ 'add-more-block--compact': fileCount > 0 }"
    >
      <template v-if="fileCount === 0">
        <feather-icon
          icon="UploadCloudIcon"
          size="36"
          class="upload-cloud-icon"
        />
        <h4>
          Drag &amp; drop files here or
          <span
            class="browse-link"
            role="button"
            tabindex="0"
            @click.stop="openFileDialog"
            @keydown.enter.prevent="openFileDialog"
          >browse</span>
        </h4>
        <p>Recommended: 1080 x 1080 px | Max size: {{ maxSizeLabel }} per file.</p>
        <p
          v-if="imageFormatHint"
          class="hint-format"
        >
          {{ imageFormatHint }}
        </p>
        <p class="hint-max">
          Up to {{ maxFiles }} images or videos.
        </p>
      </template>
      <template v-else>
        <h4 class="add-more-heading">
          Add more or
          <span
            class="browse-link"
            role="button"
            tabindex="0"
            @click.stop="openFileDialog"
            @keydown.enter.prevent="openFileDialog"
          >Browse</span>
        </h4>
        <p class="hint-max">
          {{ slotsLeft }} slot(s) left (max {{ maxFiles }} files)
        </p>
      </template>
    </div>

    <p
      v-else
      class="max-reached mb-0"
    >
      Maximum {{ maxFiles }} files uploaded. Remove one to add another.
    </p>

    <p
      v-if="error"
      class="text-danger small mb-0 mt-1"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
import {
  AD_IMAGE_EXTENSIONS,
  AD_IMAGE_FORMAT_ERROR_MESSAGE,
  AD_IMAGE_FORMAT_HINT,
  AD_MEDIA_MAX_BYTES,
  AD_MEDIA_MAX_MB,
  AD_VIDEO_EXTENSIONS,
  AD_VIDEO_FORMAT_ERROR_MESSAGE,
  AD_VIDEO_FORMAT_HINT,
  isAdMediaWithinSizeLimit,
  isAdVideoFile,
  isAllowedAdImageFile,
  isAllowedAdVideoFile,
} from '@/utils/adCreate'

const MAX_BYTES = AD_MEDIA_MAX_BYTES

export default {
  name: 'AdMediaDropZone',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Array,
      default() {
        return []
      },
      validator(v) {
        if (!Array.isArray(v)) return false
        return v.every(item => typeof File !== 'undefined' && item instanceof File)
      },
    },
    adType: {
      type: String,
      default: 'Image',
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      isDragging: false,
      error: '',
      previewUrls: [],
    }
  },
  computed: {
    normalizedFiles() {
      const v = this.value
      if (!Array.isArray(v)) return []
      return v.slice(0, this.maxFiles)
    },
    fileCount() {
      return this.normalizedFiles.length
    },
    slotsLeft() {
      return Math.max(0, this.maxFiles - this.fileCount)
    },
    isVideoAd() {
      return String(this.adType || '').toLowerCase() === 'video'
    },
    acceptAttribute() {
      if (this.isVideoAd) {
        return [...AD_VIDEO_EXTENSIONS.map(ext => `.${ext}`), 'video/*'].join(',')
      }
      return [...AD_IMAGE_EXTENSIONS.map(ext => `.${ext}`), 'image/*', 'image/svg+xml'].join(',')
    },
    imageFormatHint() {
      return this.isVideoAd
        ? `Allowed formats: ${AD_VIDEO_FORMAT_HINT}`
        : `Allowed formats: ${AD_IMAGE_FORMAT_HINT}`
    },
    maxSizeLabel() {
      return `${AD_MEDIA_MAX_MB}MB`
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler() {
        this.syncPreviews()
      },
    },
    adType() {
      this.error = ''
      if (this.fileCount) {
        this.$emit('input', [])
      }
    },
  },
  beforeDestroy() {
    this.revokeAllPreviews()
  },
  methods: {
    previewKey(file, idx) {
      return `${idx}-${file.name}-${file.size}-${file.lastModified}`
    },
    isImageFile(file) {
      return isAllowedAdImageFile(file)
    },
    isVideoFile(file) {
      return isAdVideoFile(file)
    },
    syncPreviews() {
      this.revokeAllPreviews()
      this.previewUrls = this.normalizedFiles.map(f => URL.createObjectURL(f))
    },
    revokeAllPreviews() {
      this.previewUrls.forEach(url => {
        try {
          URL.revokeObjectURL(url)
        } catch (e) { /* noop */ }
      })
      this.previewUrls = []
    },
    removeAt(idx) {
      this.error = ''
      this.$emit('format-error', '')
      const next = this.normalizedFiles.filter((_, i) => i !== idx)
      this.$emit('input', next)
    },
    validateSingleFile(file) {
      if (!isAdMediaWithinSizeLimit(file, MAX_BYTES)) {
        return `Each file must be ${AD_MEDIA_MAX_MB}MB or smaller.`
      }
      if (this.isVideoAd && !isAllowedAdVideoFile(file)) {
        return AD_VIDEO_FORMAT_ERROR_MESSAGE
      }
      if (!this.isVideoAd && !isAllowedAdImageFile(file)) {
        return AD_IMAGE_FORMAT_ERROR_MESSAGE
      }
      return ''
    },
    addFilesFromList(fileList) {
      const incoming = Array.from(fileList || []).filter(Boolean)
      if (!incoming.length) return

      const next = [...this.normalizedFiles]
      const beforeLen = next.length
      let firstError = ''

      for (const file of incoming) {
        if (next.length >= this.maxFiles) {
          firstError = firstError || `You can upload at most ${this.maxFiles} files.`
          break
        }
        const err = this.validateSingleFile(file)
        if (err) {
          firstError = firstError || err
          continue
        }
        next.push(file)
      }

      const added = next.length - beforeLen
      if (added > 0) {
        this.$emit('input', next)
        if (firstError) {
          this.error = firstError
          this.$emit('format-error', firstError)
        } else {
          this.error = ''
          this.$emit('format-error', '')
        }
      } else if (firstError) {
        this.error = firstError
        this.$emit('format-error', firstError)
      }
    },
    openFileDialog() {
      if (this.fileCount >= this.maxFiles) return
      this.error = ''
      this.$emit('format-error', '')
      this.$refs.fileInput.click()
    },
    onZoneClick(e) {
      if (this.fileCount >= this.maxFiles) return
      if (e.target.closest('.browse-link')) return
      if (e.target.closest('.media-preview-clear')) return
      if (e.target.closest('.media-preview-grid')) return
      this.openFileDialog()
    },
    onDragLeave(e) {
      const el = e.currentTarget
      if (el && e.relatedTarget && el.contains(e.relatedTarget)) return
      this.isDragging = false
    },
    onDrop(e) {
      this.isDragging = false
      if (this.fileCount >= this.maxFiles) return
      this.addFilesFromList(e.dataTransfer.files)
    },
    onInputChange(e) {
      this.addFilesFromList(e.target.files)
      e.target.value = ''
    },
  },
}
</script>

<style scoped lang="scss">
.ad-media-drop-zone {
  user-select: none;
  transition: background 0.15s ease, border-color 0.15s ease;

  &.is-empty {
    cursor: pointer;
  }

  &.has-files {
    min-height: auto !important;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 1rem 1.25rem;
  }

  &.at-max:not(.is-dragover) {
    cursor: default;
  }

  &.is-dragover {
    background: rgba(15, 128, 147, 0.06);
    border-color: #0f8093 !important;
  }

  .browse-link {
    color: #2563eb;
    cursor: pointer;
    text-decoration: none;
    font-weight: 600;
  }

  .upload-cloud-icon {
    color: #3b82f6;
    margin-bottom: 0.65rem;
  }

  .media-preview-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .media-preview-cell {
    text-align: center;
    max-width: 120px;
  }

  .media-preview-box {
    position: relative;
    width: 120px;
    height: 120px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #d8d6e0;
    background: #f3f2f7;
    margin: 0 auto;
  }

  .media-preview-img,
  .media-preview-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .media-preview-video {
    pointer-events: none;
  }

  .media-preview-clear {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(17, 24, 39, 0.65);
    color: #fff;
    line-height: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  }

  .media-preview-clear:hover,
  .media-preview-clear:focus {
    background: rgba(17, 24, 39, 0.88);
    outline: none;
  }

  .media-file-name {
    margin-top: 0.4rem;
    margin-bottom: 0;
    color: #5e5873;
    font-size: 0.75rem;
    max-width: 120px;
  }

  .add-more-block {
    text-align: center;
    border: 2px dashed #c5c9d4;
    border-radius: 8px;
    padding: 1.25rem 1rem;
    cursor: pointer;
  }

  .add-more-block--compact {
    padding: 0.85rem 1rem;
  }

  .add-more-heading {
    font-size: 1.15rem;
    margin-bottom: 0.35rem;
    font-weight: 500;
  }

  .hint-max,
  .hint-format {
    color: #7a758f;
    font-size: 0.9rem;
    margin-bottom: 0;
  }

  .hint-format {
    margin-top: 0.25rem;
  }

  .max-reached {
    text-align: center;
    color: #5e5873;
    font-size: 0.95rem;
    padding: 0.5rem 0;
  }
}
</style>
